import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import NotificationContainer from './components/NotificationContainer';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import Logout from './pages/Logout';
import VerifyEmail from './pages/VerifyEmail';
import EmailVerified from './pages/EmailVerified';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Students from './pages/Students';
import Teachers from './pages/Teachers';
import Performance from './pages/Performance';
import Attendance from './pages/Attendance';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import NotFound from './pages/NotFound';

// Teacher Pages
import AttendanceMarking from './pages/teacher/AttendanceMarking';
import AttendanceReports from './pages/teacher/AttendanceReports';
import PerformanceRecording from './pages/teacher/PerformanceRecording';
import PerformanceReports from './pages/teacher/PerformanceReports';
import EnrolledStudents from './pages/teacher/EnrolledStudents';
import MyCourses from './pages/teacher/MyCourses';
import AIPredictions from './pages/teacher/AIPredictions';
import AIAnalytics from './pages/AIAnalytics';

// Context
import { AuthProvider, useAuth } from './utils/AuthContext';
import { ThemeProvider } from './utils/ThemeContext';
import { NotificationProvider } from './utils/NotificationContext';
import { SidebarProvider, useSidebar } from './utils/SidebarContext';
import PageTracker from './utils/PageTracker';

// Main content wrapper that adjusts based on sidebar state
function MainContent() {
  const { isOpen } = useSidebar();
  const { isAuthenticated } = useAuth();

  return (
    <main 
      className={`flex-1 transition-all duration-300 ease-in-out ${
        isAuthenticated && isOpen ? 'ml-0 md:ml-64' : 'ml-0'
      }`}
    >
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Email verification routes — always public */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-email/:token" element={<EmailVerified />} />

        {/* Logout Route - accessible to authenticated users */}
        <Route path="/logout" element={<Logout />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <Courses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:id"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/students"
          element={
            <ProtectedRoute>
              <Students />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers"
          element={
            <ProtectedRoute>
              <Teachers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance"
          element={
            <ProtectedRoute>
              <Performance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <ProtectedRoute>
              <Attendance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Teacher Routes */}
        <Route
          path="/teacher/my-courses"
          element={
            <ProtectedRoute>
              <MyCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/attendance/mark"
          element={
            <ProtectedRoute>
              <AttendanceMarking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/attendance/reports"
          element={
            <ProtectedRoute>
              <AttendanceReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/performance/record"
          element={
            <ProtectedRoute>
              <PerformanceRecording />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/performance/reports"
          element={
            <ProtectedRoute>
              <PerformanceReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/enrolled-students"
          element={
            <ProtectedRoute>
              <EnrolledStudents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-analytics"
          element={
            <ProtectedRoute>
              <AIAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/ai-predictions"
          element={
            <ProtectedRoute>
              <AIPredictions />
            </ProtectedRoute>
          }
        />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
}
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Public Route Component (redirect to dashboard if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated } = useAuth();
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-neutral-50 flex flex-col">
        <Navbar />
        <Sidebar />
        <PageTracker />
        <MainContent />
        <Footer />
        <NotificationContainer />
      </div>
    </SidebarProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
